import React, { useState } from "react";
import "../../../../index.css";
import { Form, Input, message, Modal } from "antd";
import I18n from "../../../../../../i18n";
import {
  Loading,
  PasswordValidationStatus,
  User,
  UserRepository,
  validatePassword,
} from "../../../../../../../domain";
import { ServiceLocater, Services } from "../../../../../../../service-locater";

const { Item, useForm } = Form;

type Props = { visible: boolean; onClose: () => void; currentUserInfo?: User };

const EditPasswordModal: React.FC<Props> = ({ visible, onClose }) => {
  const [form] = useForm();

  const [passwordValidationStatus, setPasswordValidationStatus] = useState<
    PasswordValidationStatus | "invalid"
  >("invalid");
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [loading, setLoading] = useState<Loading>("idle");

  const submit = async () => {
    if (passwordValidationStatus === "valid") {
      setLoading("pending");
      const res = await ServiceLocater.get<UserRepository>(
        Services.UserRepository
      ).edit({ password: form.getFieldValue("password") });
      res.fold(
        (l) => {
          setLoading("failed");
          message.error(I18n.general.networkError);
        },
        (_) => {
          setLoading("succeeded");
          onClose();
        }
      );
    } else {
      setHasSubmitted(true);
    }
  };

  const getPasswordErrorText = (): string | undefined => {
    if (!hasSubmitted) return undefined;
    if (form.getFieldValue("password") === "")
      return I18n.general.passwordErrors.empty;
    if (passwordValidationStatus === "valid") return undefined;
    if (passwordValidationStatus === "short")
      return I18n.general.passwordErrors.short;
  };

  const getPasswordValidationStatus = (): "error" | undefined => {
    if (!hasSubmitted) return undefined;
    if (passwordValidationStatus === "repeatPasswordNoMatch") return undefined;
    if (passwordValidationStatus !== "valid") return "error";
    return undefined;
  };

  const getRepeatPasswordErrorText = (): string | undefined => {
    if (!hasSubmitted || getPasswordValidationStatus() !== undefined)
      return undefined;
    const repeatPassword = form.getFieldValue("repeat-password");
    if (repeatPassword === "" || repeatPassword === undefined)
      return I18n.general.passwordErrors.repeatPasswordEmpty;
    if (passwordValidationStatus === "valid") return undefined;
    if (passwordValidationStatus === "repeatPasswordNoMatch")
      return I18n.general.passwordErrors.repeatPasswordDontMatch;
  };

  const getRepeatPasswordValidationStatus = (): "error" | undefined => {
    if (!hasSubmitted) return undefined;
    if (passwordValidationStatus === "repeatPasswordNoMatch") return "error";
    return undefined;
  };

  const isLoading = loading === "pending";
  console.log(form.getFieldValue("repeat-password")?.length);

  return (
    <Modal
      title={I18n.profile.editProfile.editPassword}
      visible={visible}
      closable={!isLoading}
      maskClosable={!isLoading}
      onCancel={
        isLoading
          ? undefined
          : () => {
              setHasSubmitted(false);
              setPasswordValidationStatus("invalid");
              form.setFields([
                { name: "password", value: "" },
                { name: "repeat-password", value: "" },
              ]);
              onClose();
            }
      }
      onOk={submit}
      confirmLoading={isLoading}
      width={335}
      bodyStyle={{ paddingBottom: 0 }}
    >
      <Form form={form}>
        <Item
          name="password"
          help={getPasswordErrorText()}
          validateStatus={getPasswordValidationStatus()}
        >
          <Input.Password
            className={
              form.getFieldValue("password") === undefined
                ? ""
                : "account-info-edit-password"
            }
            disabled={isLoading}
            placeholder={I18n.general.password}
            spellCheck={false}
            onChange={(value) => {
              const repeatPassword = form.getFieldValue("repeat-password");
              setPasswordValidationStatus(
                validatePassword(
                  value.target.value,
                  repeatPassword === undefined ? "" : repeatPassword
                )
              );
              form.setFields([{ name: "password", value: value.target.value }]);
            }}
            required={true}
          />
        </Item>
        <Item
          name="repeat-password"
          help={getRepeatPasswordErrorText()}
          validateStatus={getRepeatPasswordValidationStatus()}
        >
          <Input.Password
            className={
              form.getFieldValue("repeat-password") === undefined ||
              form.getFieldValue("repeat-password") === ""
                ? ""
                : "account-info-edit-password"
            }
            disabled={isLoading}
            placeholder={I18n.general.repeatPassword}
            spellCheck={false}
            onChange={(value) => {
              const password = form.getFieldValue("password");
              setPasswordValidationStatus(
                validatePassword(
                  password === undefined ? "" : password,
                  value.target.value
                )
              );
              form.setFields([
                { name: "repeat-password", value: value.target.value },
              ]);
            }}
            required={true}
          />
        </Item>
      </Form>
    </Modal>
  );
};

export default EditPasswordModal;
